import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { 
  Users, Plus, Search, X, Edit, Trash2, Phone, Mail, MapPin, FileText,
  DollarSign, ShoppingCart, Star, Clock, TrendingUp, ArrowLeft, Save, AlertTriangle, User
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Customers = () => {
  const { storeDomain, adminPath } = useParams();
  const { user, logout, getAuthHeader, getAdminBasePath, loading: authLoading, hasPermission } = useAuth();
  const navigate = useNavigate();
  const basePath = adminPath ? `/${adminPath}` : (storeDomain ? `/admin/${storeDomain}` : getAdminBasePath());

  const [customers, setCustomers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [form, setForm] = useState({ name: '', phone: '', cedula: '', email: '', address: '', notes: '' });
  const [saving, setSaving] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerDetail, setCustomerDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [view, setView] = useState('list'); // list | stats

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate(adminPath ? `/${adminPath}/login` : '/admin/login'); return; }
    if ((adminPath && user.admin_path && adminPath !== user.admin_path) || (storeDomain && user.store_domain && storeDomain !== user.store_domain)) {
      logout(); navigate(adminPath ? `/${adminPath}/login` : `/admin/${storeDomain}/login`, { replace: true }); return;
    }
    loadCustomers();
    loadStats();
  }, [user, authLoading]);

  const loadCustomers = useCallback(async () => {
    try {
      const q = searchTerm ? `?search=${encodeURIComponent(searchTerm)}` : '';
      const res = await axios.get(`${API}/customers${q}`, { headers: getAuthHeader() });
      setCustomers(res.data);
    } catch { toast.error('Error cargando clientes'); }
    finally { setLoading(false); }
  }, [getAuthHeader, searchTerm]);

  const loadStats = async () => {
    try {
      const res = await axios.get(`${API}/customers-stats`, { headers: getAuthHeader() });
      setStats(res.data);
    } catch {}
  };

  useEffect(() => { const t = setTimeout(() => loadCustomers(), 300); return () => clearTimeout(t); }, [searchTerm]);

  const openCreate = () => { setEditingCustomer(null); setForm({ name: '', phone: '', cedula: '', email: '', address: '', notes: '' }); setShowForm(true); };
  const openEdit = (c) => { setEditingCustomer(c); setForm({ name: c.name, phone: c.phone || '', cedula: c.cedula || '', email: c.email || '', address: c.address || '', notes: c.notes || '' }); setShowForm(true); };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error('El nombre es obligatorio'); return; }
    setSaving(true);
    try {
      if (editingCustomer) {
        await axios.put(`${API}/customers/${editingCustomer.id}`, form, { headers: getAuthHeader() });
        toast.success('Cliente actualizado');
      } else {
        await axios.post(`${API}/customers`, form, { headers: getAuthHeader() });
        toast.success('Cliente creado');
      }
      setShowForm(false);
      loadCustomers();
      loadStats();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    try {
      await axios.delete(`${API}/customers/${deleteConfirm.id}`, { headers: getAuthHeader() });
      toast.success('Cliente eliminado');
      setDeleteConfirm(null);
      if (selectedCustomer?.id === deleteConfirm.id) { setSelectedCustomer(null); setCustomerDetail(null); }
      loadCustomers(); loadStats();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); setDeleteConfirm(null); }
  };

  const viewCustomer = async (c) => {
    setSelectedCustomer(c);
    setLoadingDetail(true);
    try {
      const res = await axios.get(`${API}/customers/${c.id}`, { headers: getAuthHeader() });
      setCustomerDetail(res.data);
    } catch { toast.error('Error cargando detalle'); }
    finally { setLoadingDetail(false); }
  };

  const formatDate = (d) => d ? new Date(typeof d === 'string' && !d.endsWith('Z') ? d + 'Z' : d).toLocaleDateString('es-EC', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'America/Guayaquil' }) : 'Nunca';

  if (authLoading || loading) return (
    <div className="min-h-screen flex items-center justify-center"><Users size={48} className="animate-spin text-accent-text" /></div>
  );

  return (
    <div className="min-h-screen" style={{ background: 'var(--bg-section)' }}>
      {/* Header */}
      <div className="bg-white border-b" style={{ borderColor: 'var(--border-light)' }}>
        <div className="container py-3">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <Users className="text-accent-text" size={24} />
              <div><h1 className="text-base md:text-lg font-bold">Clientes</h1><p className="text-xs text-text-muted">{customers.length} registrados</p></div>
            </div>
            <div className="flex gap-2">
              <button onClick={() => navigate(`${basePath}/dashboard`)} className="btn-secondary text-sm"><ArrowLeft size={16} className="mr-1" /> Productos</button>
              <button onClick={() => navigate(`${basePath}/pos`)} className="btn-secondary text-sm">POS</button>
            </div>
          </div>
        </div>
      </div>

      <div className="container py-4">
        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
            <div className="bg-white p-4 rounded-xl border" style={{ borderColor: 'var(--border-light)' }}>
              <p className="text-xs text-text-muted">Total Clientes</p>
              <p className="text-2xl font-bold text-accent-text" data-testid="stat-total">{stats.total_customers}</p>
            </div>
            <div className="bg-white p-4 rounded-xl border" style={{ borderColor: 'var(--border-light)' }}>
              <p className="text-xs text-text-muted">Clientes Activos</p>
              <p className="text-2xl font-bold" style={{ color: '#22c55e' }}>{stats.active_customers}</p>
            </div>
            <div className="bg-white p-4 rounded-xl border" style={{ borderColor: 'var(--border-light)' }}>
              <p className="text-xs text-text-muted">Ingresos de Clientes</p>
              <p className="text-2xl font-bold" style={{ color: '#3b82f6' }}>${stats.total_revenue_from_clients.toFixed(2)}</p>
            </div>
            <div className="bg-white p-4 rounded-xl border" style={{ borderColor: 'var(--border-light)' }}>
              <p className="text-xs text-text-muted">Promedio / Cliente</p>
              <p className="text-2xl font-bold" style={{ color: '#f59e0b' }}>${stats.avg_per_client.toFixed(2)}</p>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Left: Customer List */}
          <div className="lg:col-span-1 bg-white rounded-xl border flex flex-col" style={{ borderColor: 'var(--border-light)', maxHeight: 'calc(100vh - 260px)' }}>
            <div className="p-3 border-b" style={{ borderColor: 'var(--border-light)' }}>
              <div className="flex gap-2 mb-2">
                <div className="flex-1 relative">
                  <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
                  <input type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Buscar cliente..."
                    className="w-full pl-9 pr-3 py-2 rounded-lg border text-sm" style={{ borderColor: 'var(--border-light)' }} data-testid="customer-search" />
                </div>
                <button onClick={openCreate} className="btn-primary text-xs px-3" data-testid="add-customer-btn"><Plus size={16} /></button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto">
              {customers.length === 0 ? (
                <div className="p-8 text-center"><Users size={40} className="mx-auto text-text-muted mb-2 opacity-40" /><p className="text-sm text-text-muted">No hay clientes</p></div>
              ) : customers.map(c => (
                <div key={c.id} onClick={() => viewCustomer(c)}
                  className={`p-3 border-b cursor-pointer transition-colors ${selectedCustomer?.id === c.id ? 'bg-accent-wash' : 'hover:bg-gray-50'}`}
                  style={{ borderColor: 'var(--border-light)' }} data-testid={`customer-${c.id}`}>
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0" style={{ background: 'var(--accent-primary)' }}>
                      {c.name.charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate">{c.name}</p>
                      <p className="text-xs text-text-muted">{c.phone || c.cedula || 'Sin datos'}</p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="font-bold text-sm text-accent-text">${(c.total_spent || 0).toFixed(2)}</p>
                      <p className="text-[10px] text-text-muted">{c.total_purchases || 0} compras</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right: Customer Detail */}
          <div className="lg:col-span-2 bg-white rounded-xl border" style={{ borderColor: 'var(--border-light)', maxHeight: 'calc(100vh - 260px)', overflow: 'auto' }}>
            {selectedCustomer && customerDetail ? (
              <div>
                {/* Detail Header */}
                <div className="p-5 border-b flex items-center justify-between" style={{ borderColor: 'var(--border-light)' }}>
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full flex items-center justify-center text-white text-xl font-bold" style={{ background: 'var(--accent-primary)' }}>
                      {customerDetail.name.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <h2 className="font-bold text-lg" data-testid="customer-detail-name">{customerDetail.name}</h2>
                      <p className="text-xs text-text-muted">Cliente desde {formatDate(customerDetail.created_at)}</p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => openEdit(customerDetail)} className="btn-secondary text-xs"><Edit size={14} className="mr-1" /> Editar</button>
                    <button onClick={() => setDeleteConfirm(customerDetail)} className="p-2 rounded-lg hover:bg-red-50 text-red-400"><Trash2 size={16} /></button>
                  </div>
                </div>

                {/* Info Cards */}
                <div className="p-5 grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div className="p-3 rounded-lg" style={{ background: 'var(--accent-wash)' }}>
                    <p className="text-xs text-text-muted">Total Gastado</p>
                    <p className="text-xl font-bold text-accent-text">${(customerDetail.total_spent || 0).toFixed(2)}</p>
                  </div>
                  <div className="p-3 rounded-lg" style={{ background: '#dcfce7' }}>
                    <p className="text-xs text-text-muted">Compras</p>
                    <p className="text-xl font-bold" style={{ color: '#16a34a' }}>{customerDetail.total_purchases || 0}</p>
                  </div>
                  <div className="p-3 rounded-lg" style={{ background: '#dbeafe' }}>
                    <p className="text-xs text-text-muted">Promedio</p>
                    <p className="text-xl font-bold" style={{ color: '#2563eb' }}>${(customerDetail.avg_purchase || 0).toFixed(2)}</p>
                  </div>
                  <div className="p-3 rounded-lg" style={{ background: '#fef3c7' }}>
                    <p className="text-xs text-text-muted">Ultima Compra</p>
                    <p className="text-sm font-bold" style={{ color: '#92400e' }}>{formatDate(customerDetail.last_purchase)}</p>
                  </div>
                </div>

                {/* Contact Info */}
                <div className="px-5 pb-3">
                  <div className="flex flex-wrap gap-3 text-xs">
                    {customerDetail.phone && <span className="flex items-center gap-1 px-3 py-1 rounded-full bg-gray-100"><Phone size={12} /> {customerDetail.phone}</span>}
                    {customerDetail.cedula && <span className="flex items-center gap-1 px-3 py-1 rounded-full bg-gray-100"><FileText size={12} /> {customerDetail.cedula}</span>}
                    {customerDetail.email && <span className="flex items-center gap-1 px-3 py-1 rounded-full bg-gray-100"><Mail size={12} /> {customerDetail.email}</span>}
                    {customerDetail.address && <span className="flex items-center gap-1 px-3 py-1 rounded-full bg-gray-100"><MapPin size={12} /> {customerDetail.address}</span>}
                  </div>
                  {customerDetail.notes && <p className="text-xs text-text-muted mt-2 italic">{customerDetail.notes}</p>}
                </div>

                {/* Favorites */}
                {customerDetail.favorites?.length > 0 && (
                  <div className="px-5 pb-4">
                    <h3 className="font-bold text-sm mb-2 flex items-center gap-2"><Star size={16} style={{ color: '#f59e0b' }} /> Productos Favoritos</h3>
                    <div className="flex flex-wrap gap-2">
                      {customerDetail.favorites.map((f, i) => (
                        <span key={i} className="px-3 py-1.5 rounded-lg border text-xs font-medium" style={{ borderColor: 'var(--border-light)' }}>
                          {f.name} <span className="text-text-muted ml-1">({f.times}x)</span>
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Purchase History */}
                <div className="px-5 pb-5">
                  <h3 className="font-bold text-sm mb-2 flex items-center gap-2"><Clock size={16} className="text-accent-text" /> Historial de Compras</h3>
                  {customerDetail.purchases?.length === 0 ? (
                    <p className="text-sm text-text-muted text-center py-6">Sin compras registradas</p>
                  ) : (
                    <div className="space-y-2 max-h-60 overflow-y-auto">
                      {customerDetail.purchases?.map(p => (
                        <div key={p.id} className="p-3 rounded-lg border" style={{ borderColor: 'var(--border-light)' }}>
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-xs text-text-muted">{new Date(typeof p.created_at === 'string' && !p.created_at.endsWith('Z') ? p.created_at + 'Z' : p.created_at).toLocaleDateString('es-EC', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'America/Guayaquil' })}</span>
                            <span className="font-bold text-sm text-accent-text">${(p.total || 0).toFixed(2)}</span>
                          </div>
                          <p className="text-xs text-text-muted truncate">{p.items?.map(i => `${i.product_name} x${i.quantity}`).join(', ')}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ) : loadingDetail ? (
              <div className="flex items-center justify-center py-20"><Users size={32} className="animate-spin text-accent-text" /></div>
            ) : (
              <div className="flex items-center justify-center py-20 text-center">
                <div><Users size={48} className="mx-auto text-text-muted mb-3 opacity-40" /><p className="font-medium text-text-muted">Selecciona un cliente</p><p className="text-sm text-text-muted mt-1">para ver su perfil y compras</p></div>
              </div>
            )}
          </div>
        </div>

        {/* Top Buyers (below) */}
        {stats?.top_buyers?.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-5">
            <div className="bg-white rounded-xl border p-4" style={{ borderColor: 'var(--border-light)' }}>
              <h3 className="font-bold text-sm mb-3 flex items-center gap-2"><TrendingUp size={16} style={{ color: '#22c55e' }} /> Top Compradores (Monto)</h3>
              <div className="space-y-2">
                {stats.top_buyers.map((c, i) => (
                  <div key={c.id} className="flex items-center gap-3">
                    <span className="w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-bold" style={{ background: i < 3 ? '#f59e0b' : '#d1d5db' }}>{i + 1}</span>
                    <p className="flex-1 text-sm font-medium truncate">{c.name}</p>
                    <p className="font-bold text-sm text-accent-text">${(c.total_spent || 0).toFixed(2)}</p>
                  </div>
                ))}
              </div>
            </div>
            {stats?.inactive?.length > 0 && (
              <div className="bg-white rounded-xl border p-4" style={{ borderColor: 'var(--border-light)' }}>
                <h3 className="font-bold text-sm mb-3 flex items-center gap-2"><AlertTriangle size={16} style={{ color: '#f97316' }} /> Clientes Inactivos (+30 dias)</h3>
                <div className="space-y-2">
                  {stats.inactive.map(c => (
                    <div key={c.id} className="flex items-center gap-3">
                      <User size={16} className="text-text-muted" />
                      <p className="flex-1 text-sm truncate">{c.name}</p>
                      <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: '#fff7ed', color: '#c2410c' }}>{c.days_inactive}d</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {showForm && (
        <>
          <div className="fixed inset-0 bg-black bg-opacity-50 z-40" onClick={() => setShowForm(false)} />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-md w-full" data-testid="customer-form-modal">
              <div className="p-5 border-b" style={{ borderColor: 'var(--border-light)' }}>
                <h2 className="font-bold text-lg">{editingCustomer ? 'Editar Cliente' : 'Nuevo Cliente'}</h2>
              </div>
              <form onSubmit={handleSave} className="p-5 space-y-3">
                <div>
                  <label className="block text-sm font-medium mb-1">Nombre *</label>
                  <input type="text" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required
                    className="w-full px-4 py-2.5 rounded-lg border" style={{ borderColor: 'var(--border-light)' }} placeholder="Nombre completo" data-testid="customer-name-input" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium mb-1">Telefono</label>
                    <input type="text" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })}
                      className="w-full px-4 py-2.5 rounded-lg border" style={{ borderColor: 'var(--border-light)' }} placeholder="0991234567" data-testid="customer-phone-input" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Cedula / RUC</label>
                    <input type="text" value={form.cedula} onChange={e => setForm({ ...form, cedula: e.target.value })}
                      className="w-full px-4 py-2.5 rounded-lg border" style={{ borderColor: 'var(--border-light)' }} placeholder="1234567890" data-testid="customer-cedula-input" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Email</label>
                  <input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })}
                    className="w-full px-4 py-2.5 rounded-lg border" style={{ borderColor: 'var(--border-light)' }} placeholder="correo@email.com" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Direccion</label>
                  <input type="text" value={form.address} onChange={e => setForm({ ...form, address: e.target.value })}
                    className="w-full px-4 py-2.5 rounded-lg border" style={{ borderColor: 'var(--border-light)' }} placeholder="Direccion" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Notas</label>
                  <textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows="2"
                    className="w-full px-4 py-2.5 rounded-lg border" style={{ borderColor: 'var(--border-light)' }} placeholder="Notas internas" />
                </div>
                <div className="flex gap-3 pt-2">
                  <button type="button" onClick={() => setShowForm(false)} className="btn-secondary flex-1">Cancelar</button>
                  <button type="submit" disabled={saving} className="btn-primary flex-1" data-testid="customer-save-btn">
                    <Save size={16} className="mr-1" /> {saving ? 'Guardando...' : 'Guardar'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </>
      )}

      {/* Delete Confirm */}
      {deleteConfirm && (
        <>
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50" onClick={() => setDeleteConfirm(null)} />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl shadow-2xl p-6 max-w-sm w-full text-center">
              <Trash2 size={40} className="mx-auto text-red-500 mb-3" />
              <h3 className="font-bold text-lg mb-2">Eliminar Cliente</h3>
              <p className="text-text-muted text-sm mb-4">Eliminar a <strong>{deleteConfirm.name}</strong>? Se pierde el historial.</p>
              <div className="flex gap-3">
                <button onClick={() => setDeleteConfirm(null)} className="btn-secondary flex-1">Cancelar</button>
                <button onClick={handleDelete} className="flex-1 px-4 py-2 rounded-lg text-white font-medium" style={{ background: '#ef4444' }}>Eliminar</button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Customers;
